import React, {useRef, useState} from 'react';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {Chip, Stack, TextField} from "@mui/material";
import {BasicModalContent, LoadingScreen} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../../utils/resolveErrorMessage";
import {CheckRounded, ContentCopyRounded, RepeatRounded} from "@mui-symbols-material/w300";
import {InstalledBaseNode, InstalledBaseNodeType} from "../../../codegen/graphql";
import NodeModalTitle from "../NodeModalTitle";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import _ from "lodash";
import {InstalledBaseNodeListTdas} from "../../../graphql/queries/installedBaseNodeListTdas";
import {CreateInstalledBaseNode} from '../../../graphql/mutations/createInstalledBaseNode';
import InstalledBaseTDAForm from "../../InstalledBaseTDAForm";
import {withNodeImageInputType} from "../../NodeImageInput";
import {separateInstalledBaseNodeCreateAttributes} from "../../../utils/separateInstalledBaseNodeAttributes";
import {prepareNodeImageFormValues} from "../../../utils/nodeImage";
import {uploadSingleFile} from "../../../utils/uploadSingleFile";
import InstalledBaseNodePathBreadcrumb from "../../InstalledBaseNodePathBreadcrumb";
import NodeTypeSelectionItem from "./nodeTypeSelectionItem";
import SplitButton from "../../SplitButton";
import {FormikHelpersRef} from "../../../types";

// Custom inputType applied to the `nodeTypeKey` attribute so it renders as a read-only
// node-type card (with an edit affordance) instead of the default select.
const NODE_TYPE_CARD_INPUT_TYPE = 'nodeTypeCard';

// Custom inputType applied to the `title` attribute for pressure lines so the stock
// text input gets suggestion chips below it (create-only, by design).
const TITLE_SUGGESTIONS_INPUT_TYPE = 'titleWithSuggestions';

// English only for now — the app releases in English only.
const PRESSURE_LINE_TITLE_SUGGESTIONS = ['Forming vacuum', 'Rough vacuum', 'Fine vacuum', 'Intermediate pressure'];

// Node types that cannot be duplicated.
const NON_DUPLICATABLE_NODE_TYPES = ['ASSET', 'CLIENT'];

const getAttributeLabel = (attribute: any) => {
    let label = attribute.name;

    if (attribute?.rules?.find((rule: any) => rule.name === "required")) {
        label = label + " *"
    }

    return label
}

type SubmitMode = 'save' | 'saveAndNew' | 'saveAndDuplicate';

interface Props {
    node: InstalledBaseNode;
    refetchFunction?: () => void;
    createNodeCallback?: (nodeId: string) => void
    onClose?: () => void,
    onBack?: () => void,
    createNodeType?: InstalledBaseNodeType,
    onDuplicateRequest?: (template: { nodeId: string; title: string }) => void,
}

const CreateNodeForm = ({node, refetchFunction, createNodeCallback, onClose, createNodeType, onBack, onDuplicateRequest}: Props) => {
    const {t} = useTranslation();

    const [formikHelpers, setFormikHelpers] = useState<FormikHelpersRef | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // Remount key for the form: "Save & New" resets by remounting with fresh initial values.
    const [formKey, setFormKey] = useState(0);
    const submitModeRef = useRef<SubmitMode>('save');

    const [createNode] = useMutation(CreateInstalledBaseNode);
    const {keycloak} = useKeycloak();

    const {data, loading} = useQuery(InstalledBaseNodeListTdas, {
        variables: {
            typeDefinitionIndex: "INSTALLED_BASE_NODE_CREATE",
            options: {
                parentNodeId: node.id,
                nodeTypeKey: createNodeType?.key
            },
        },
        fetchPolicy: "network-only",
    })

    const canDuplicate = !!createNodeType && !NON_DUPLICATABLE_NODE_TYPES.includes(createNodeType.key);

    const handleSubmit = async (formValues: any) => {
        const submitMode = submitModeRef.current;
        submitModeRef.current = 'save';
        setSubmitting(true);

        // A picked node image is uploaded first; its file-service UUID becomes input.imageId.
        let preparedValues: Record<string, any>;
        try {
            preparedValues = await prepareNodeImageFormValues(
                formValues,
                null,
                (file) => uploadSingleFile(file, keycloak.token)
            );
        } catch (err) {
            toast.error(t('Image upload failed.'));
            setSubmitting(false);
            return;
        }

        const { input, dynamicLists } = separateInstalledBaseNodeCreateAttributes(
            preparedValues,
            rawAttributes
        );

        return createNode({
                variables: {
                    parentNodeId: node.id,
                    input,
                    ...Object.fromEntries(
                        Object.entries(dynamicLists).map(([key, list]) => [key, list.length > 0 ? list : undefined])
                    )
                },
            refetchQueries: ["InstalledBaseNodeList"]
            }
        ).then((res) => {
            const createdNodeId = _.get(res, 'data.createInstalledBaseNode.id')

            toast.success(t('Node has been created successfully.'));

            if (refetchFunction) {
                refetchFunction();
            }

            // "Save & New" / "Save & Duplicate" keep the modal open, so skip the
            // createNodeCallback to avoid the tree selection jumping underneath it.
            if (submitMode === 'saveAndNew') {
                setFormKey((key) => key + 1);
                return;
            }
            if (submitMode === 'saveAndDuplicate' && createdNodeId && onDuplicateRequest) {
                onDuplicateRequest({nodeId: createdNodeId, title: formValues.title});
                return;
            }

            if (createdNodeId && createNodeCallback) {
                createNodeCallback(createdNodeId);
            }
            if (onClose) {
                onClose();
            }
        }).catch((err) => {
            console.log(err, 'ERROR')
            toast.error(resolveErrorMessage(err, t))
        }).finally(() => {
            setSubmitting(false);
        })
    }

    const handleValuesChange = (values: { [key: string]: any }) => {
        // Guard against re-setting: setFieldValue creates a new values object even for
        // an unchanged value, which would re-trigger this callback in a loop.
        if (values.waferType === "BATCH" && values.strategicTool !== true) {
            formikHelpers?.setFieldValue("strategicTool", true);
        }
    };

    const submitWithMode = (mode: SubmitMode) => {
        submitModeRef.current = mode;
        formikHelpers?.submit();
    };

    const rawAttributes = _.get(data, 'installedBaseNodeList.typeDefinitionAttributeList.typeDefinitionAttributes', [])

    const isPressureLine = createNodeType?.key === 'PRESSURE_LINE';

    // Render the nodeTypeKey field as the selected-type card instead of a select. The value
    // is untouched (separation reads it from form values regardless of inputType).
    // The image TDA is re-tagged for the app's NodeImageInput (no preview on create).
    const formAttributes = withNodeImageInputType(rawAttributes.map((attr: any) => {
        if (attr.index === 'nodeTypeKey') return {...attr, inputType: NODE_TYPE_CARD_INPUT_TYPE};
        if (isPressureLine && attr.index === 'title') return {...attr, inputType: TITLE_SUGGESTIONS_INPUT_TYPE};
        return attr;
    }));

    const customInputRenderers = {
        [NODE_TYPE_CARD_INPUT_TYPE]: () =>
            createNodeType ? <NodeTypeSelectionItem nodeType={createNodeType} onEdit={onBack}/> : null,
        [TITLE_SUGGESTIONS_INPUT_TYPE]: ({attribute, value, error, onChange, onBlur, setFieldValue}: any) => (
            <Box>
                {/* Mirrors TDAForm's stock text branch (TextFieldWithUnit without a unit) */}
                <TextField
                    id={attribute.index}
                    name={attribute.index}
                    fullWidth
                    size={'small'}
                    label={getAttributeLabel(attribute)}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={Boolean(error)}
                    helperText={error}
                    InputLabelProps={{shrink: true}}
                />
                {/* Same styles as the TDAForm date-preset chips */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{pt: '5px'}}>
                    {PRESSURE_LINE_TITLE_SUGGESTIONS.map((suggestion) => (
                        <Chip
                            key={suggestion}
                            label={suggestion}
                            variant={value === suggestion ? 'filled' : 'outlined'}
                            color={value === suggestion ? 'primary' : 'default'}
                            size="small"
                            onClick={() => setFieldValue(attribute.index, suggestion)}
                        />
                    ))}
                </Stack>
            </Box>
        ),
    };

    const saveOptions = [
        {
            icon: <CheckRounded/>,
            title: t('Save'),
            subtitle: t('Save and close the dialog'),
            onClick: () => submitWithMode('save'),
        },
        {
            icon: <RepeatRounded/>,
            title: t('Save & New'),
            subtitle: t('Create another node of the same type'),
            onClick: () => submitWithMode('saveAndNew'),
        },
        ...(canDuplicate ? [{
            icon: <ContentCopyRounded/>,
            title: t('Save & Duplicate…'),
            subtitle: t('Create copies of the new node'),
            onClick: () => submitWithMode('saveAndDuplicate'),
        }] : []),
    ];

    return <BasicModalContent
        title={<NodeModalTitle prefix={t('Create node under')} node={node} labelType={'title'}/>}
        onClose={onClose}
        onBack={onBack}
    >
        <Box sx={{mt: 0.5, mb: 1}}>
            <InstalledBaseNodePathBreadcrumb nodeId={node.id}/>
        </Box>
        {
            loading ? <LoadingScreen size={"md"} /> :
                <>
                    {/* The shared TDAForm renders its own submit button whenever onSubmit is set;
                        hide it and drive submission from the custom footer below instead. The
                        hidden button keeps Enter-key submit working (= plain "Save"). */}
                    <Box sx={{'& button[type="submit"]': {display: 'none'}}}>
                        <InstalledBaseTDAForm
                            key={formKey}
                            formStyle={{paddingTop: '0px'}}
                            onSubmit={handleSubmit}
                            attributes={formAttributes}
                            initialFormValues={{nodeTypeKey: createNodeType?.key}}
                            customInputRenderers={customInputRenderers}
                            onValuesChange={handleValuesChange}
                            setFormikHelpersRef={setFormikHelpers}
                        />
                    </Box>
                    <Box sx={{display: 'flex', gap: 1}}>
                        <Box sx={{flex: 1}}>
                            <Button fullWidth variant="outlined" onClick={onClose}>
                                {t('Cancel')}
                            </Button>
                        </Box>
                        <Box sx={{flex: 1}}>
                            <SplitButton
                                fullWidth
                                primaryText={t('Save')}
                                onPrimaryClick={() => submitWithMode('save')}
                                options={saveOptions}
                                loading={submitting}
                            />
                        </Box>
                    </Box>
                </>
        }
    </BasicModalContent>
};

export default CreateNodeForm;
